import type { LoomSurfaceAppearance } from "./loomViewportSurfaceMaterial";
import "./loom-control-surfaces.css";

export type LoomInspectionSurfaceKind =
  | "symmetric-explicit-trim"
  | "provisional-explicit-union"
  | "gpu-implicit-union"
  | "authored-operands";

interface LoomSurfaceAppearanceControlProps {
  appearance: LoomSurfaceAppearance;
  onChange: (appearance: LoomSurfaceAppearance) => void;
  surfaceKind: LoomInspectionSurfaceKind;
}

function surfaceAuthority(surfaceKind: LoomInspectionSurfaceKind): string {
  if (surfaceKind === "provisional-explicit-union") return "provisional-explicit-union-not-accepted";
  if (surfaceKind === "symmetric-explicit-trim") return "symmetric-explicit-trim-geometry-only";
  if (surfaceKind === "gpu-implicit-union") return "gpu-implicit-classified-surfaces-visual-only";
  return "authored-operands-no-explicit-surface";
}

export function LoomSurfaceAppearanceControl({
  appearance,
  onChange,
  surfaceKind,
}: LoomSurfaceAppearanceControlProps) {
  const available = surfaceKind !== "authored-operands";
  const effectiveAppearance = available ? appearance : "opaque";
  const ghost = effectiveAppearance === "ghost-inspection";
  const label = ghost ? "Transparent" : "Solid";
  return (
    <div
      className="loom-surface-appearance-control is-compact"
      data-surface-appearance={effectiveAppearance}
      data-surface-authority={surfaceAuthority(surfaceKind)}
      data-surface-kind={surfaceKind}
      data-testid="loom-surface-appearance-control"
    >
      <div aria-label="Surface appearance" className="loom-presentation-segment" role="group">
        <button
          aria-describedby="loom-surface-appearance-status"
          aria-pressed={!ghost}
          disabled={!available}
          onClick={() => { if (ghost) onChange("opaque"); }}
          type="button"
        >Solid</button>
        <button
          aria-describedby="loom-surface-appearance-status"
          aria-pressed={ghost}
          data-testid="loom-ghost-inspection-toggle"
          disabled={!available}
          onClick={() => { if (!ghost) onChange("ghost-inspection"); }}
          type="button"
        >Transparent</button>
      </div>
      <span
        aria-live="polite"
        className="loom-sr-only"
        data-testid="loom-surface-appearance-status"
        id="loom-surface-appearance-status"
      >{label}</span>
    </div>
  );
}
